import type { FilterSpecification, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useMemo, useState } from 'react';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import type { WaypointCategoryUid } from '@alltrails/types/Waypoint';
import { useAppDispatch, useAppSelector } from '../../redux';
import { clearSelectedWaypoint, setSelectedWaypoint, updateCursor } from '../../redux/reducer';
import { communityWaypointsId } from '../../utils/layers';
import WaypointPinLayer from './WaypointPinLayer';

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;

const sourceId = 'community-waypoints-source';
// Vector tile source-layer name as served by the backend.
const sourceLayer = 'communityWaypoints';

type CommunityWaypointFeatureProperties = {
  waypoint_id: number | string;
  name?: string;
  description?: string;
  category_uid?: WaypointCategoryUid;
  originally_created_at?: string;
};

const CommunityWaypoints = () => {
  const dispatch = useAppDispatch();
  const publicBaseUrl = usePublicBaseUrl();

  const selectedCommunityWaypointId = useAppSelector(state =>
    state.map.selectedWaypoint?.source === 'community' ? state.map.selectedWaypoint.data.id : undefined
  );
  const communityWaypointCategories = useAppSelector(state => state.map.communityWaypointCategories);

  const [activeWaypointId, setActiveWaypointId] = useState<number | undefined>(undefined);

  const tiles = useMemo(() => [`${publicBaseUrl}/wmts/overlay/communityWaypoints/{z}/{x}/{y}?key=${apiKey}`], [publicBaseUrl]);

  const categoryFilter: FilterSpecification = useMemo(
    () => ['in', ['get', 'category_uid'], ['literal', communityWaypointCategories]],
    [communityWaypointCategories]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (!event.features?.length) {
        return;
      }
      const feature = event.features[0];
      if (feature.geometry.type !== 'Point') {
        return;
      }
      const props = feature.properties as CommunityWaypointFeatureProperties | null;
      if (!props || props.waypoint_id === undefined) {
        return;
      }
      const [longitude, latitude] = feature.geometry.coordinates;
      // Mapbox encodes vector tile properties as strings — coerce id to number.
      const id = typeof props.waypoint_id === 'number' ? props.waypoint_id : Number(props.waypoint_id);
      if (Number.isNaN(id)) {
        return;
      }

      event.preventDefault();

      // Toggle off if the user clicks the same pin again (mirrors UserWaypoints).
      if (selectedCommunityWaypointId === id) {
        dispatch(clearSelectedWaypoint());
        return;
      }

      dispatch(
        setSelectedWaypoint({
          source: 'community',
          data: {
            id,
            name: props.name ?? '',
            description: props.description,
            latitude,
            longitude,
            categoryUid: props.category_uid,
            createdAt: props.originally_created_at
          }
        })
      );
    },
    [dispatch, selectedCommunityWaypointId]
  );

  const onHover = useCallback(
    (props: CommunityWaypointFeatureProperties) => {
      const id = typeof props.waypoint_id === 'number' ? props.waypoint_id : Number(props.waypoint_id);
      if (!Number.isNaN(id)) {
        setActiveWaypointId(id);
      }
      dispatch(updateCursor('pointer'));
    },
    [dispatch]
  );

  const onHoverEnd = useCallback(() => {
    setActiveWaypointId(undefined);
    dispatch(updateCursor('unset'));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50 /* debounceTime */);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (!onMouseEnter || !event.features?.length) {
        return;
      }
      onMouseEnter(event.features[0].properties as CommunityWaypointFeatureProperties);
    },
    [onMouseEnter]
  );

  // Hover wins over selection for the enlarged-pin highlight (matches UserWaypoints).
  const composedActiveWaypointId = activeWaypointId ?? selectedCommunityWaypointId;

  return (
    <WaypointPinLayer
      activeWaypointId={composedActiveWaypointId}
      filter={categoryFilter}
      labelVisibility="when-active"
      layerId={communityWaypointsId}
      minZoom={7}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      properties={{ id: 'waypoint_id', categoryUid: 'category_uid', title: 'name' }}
      source={{ type: 'vector', tiles, sourceLayer }}
      sourceId={sourceId}
      variant="community"
    />
  );
};

export default CommunityWaypoints;
